import { useState, useEffect } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { Stack } from '@leroy-merlin-br/backyard-react-ui'

import { Actions } from 'lmcv/redux/modules/coupon'
import { Transition } from 'lmcv/components/Transition'
import { CouponContainer } from 'lmcv/containers/Coupon'

import * as S from '../../styled'
import LoadMore from './components/LoadMore'
import FilterButtons from './components/FilterButtons'
import { VouchersSkeleton } from '../Skeleton'

const ListVouchers = ({ vouchers }) => {
  const [allAvailable, setAllAvailable] = useState([])
  const [allActivated, setAllActivated] = useState([])

  const [isUpdated, setIsUpdated] = useState(false)
  const [filterType, setFilterType] = useState('available')
  const [pagination, setPagination] = useState(2)
  const [hasMoreContent, setHasMoreContent] = useState(true)

  const dispatch = useDispatch()
  const { percentagePaginated, percentage, activated } = useSelector(
    state => state.coupon,
    shallowEqual
  )

  const hasMoreItemsToLoad =
    percentagePaginated.payload.length > 0 && !percentagePaginated.isRequest

  const filterTypes = {
    available: filterType === 'available',
    activated: filterType === 'activated'
  }

  const changeFilterType = selectedFilter => {
    if (selectedFilter === 'Disponíveis') {
      return setFilterType('available')
    }

    setFilterType('activated')
  }

  const filterByAvailable = e => {
    dispatch(Actions.getPercentage(1, 'available', 15))
    changeFilterType(e.target.textContent)
    setHasMoreContent(true)
  }

  const filterByActivated = e => {
    dispatch(Actions.getActivated(1, 'advantages', 15))
    changeFilterType(e.target.textContent)
    setHasMoreContent(true)
  }

  const getMoreVouchers = () => {
    if (filterTypes.available) {
      dispatch(Actions.getPercentagePaginated(pagination, 'available', 15))
      setPagination(pagination + 1)
      setIsUpdated(true)
      return
    }

    if (filterTypes.activated) {
      dispatch(Actions.getPercentagePaginated(pagination, 'active', 15))
      setPagination(pagination + 1)
      setIsUpdated(true)
    }
  }

  useEffect(() => {
    if (filterTypes.available) {
      setPagination(2)
      setAllAvailable(percentage.payload.filter(item => !item.isActive))
    }
  }, [filterTypes.available, percentage.payload])

  useEffect(() => {
    if (filterTypes.activated) {
      setPagination(2)
      setAllActivated(activated.payload.filter(item => item.isActive))
    }
  }, [filterTypes.activated, activated.payload])

  useEffect(() => {
    const setInitialVouchers = () => {
      const availableVouchers = vouchers.payload.filter(
        item => !item.isActive && item
      )
      setAllAvailable([...availableVouchers])
    }
    setInitialVouchers()
  }, [vouchers])

  useEffect(() => {
    const setMoreAvailableVouchers = () => {
      const filterIsAvailable = filterTypes.available && isUpdated

      if (hasMoreItemsToLoad && filterIsAvailable) {
        setAllAvailable([
          ...allAvailable,
          ...percentagePaginated.payload.filter(item => !item.isActive)
        ])
        setIsUpdated(false)
      }
    }
    setMoreAvailableVouchers()
  }, [
    isUpdated,
    allAvailable,
    filterTypes.available,
    percentagePaginated.payload,
    hasMoreItemsToLoad
  ])

  useEffect(() => {
    const setMoreActiveVouchers = () => {
      const filterIsActive = filterTypes.activated && isUpdated

      if (hasMoreItemsToLoad && filterIsActive) {
        setAllActivated([
          ...allActivated,
          ...percentagePaginated.payload.filter(item => item.isActive)
        ])
        setIsUpdated(false)
      }
    }
    setMoreActiveVouchers()
  }, [
    isUpdated,
    allActivated,
    filterTypes.activated,
    percentagePaginated.payload,
    hasMoreItemsToLoad
  ])

  useEffect(() => {
    const hideShowMoreButton = () => {
      const validateVouchersRequest =
        !percentagePaginated.isRequest && isUpdated
      const validateVouchersQuantity = !percentagePaginated.payload.length

      if (validateVouchersQuantity && validateVouchersRequest) {
        setHasMoreContent(false)
        setIsUpdated(false)
      }
    }
    hideShowMoreButton()
  }, [isUpdated, percentagePaginated.payload, percentagePaginated.isRequest])

  if (vouchers.isRequest || activated.isRequest || percentage.isRequest) {
    return (
      <Transition.Fade>
        <VouchersSkeleton size={4} />
      </Transition.Fade>
    )
  }

  return (
    <Stack>
      {!percentagePaginated.isRequest && (
        <Transition.Fade>
          <FilterButtons
            currentType={filterType}
            filterTypes={filterTypes}
            filterByActivated={filterByActivated}
            filterByAvailable={filterByAvailable}
          />
        </Transition.Fade>
      )}
      <Transition.Fade>
        <S.ListContainer>
          {filterTypes.available &&
            allAvailable.map(voucher => (
              <S.ListItem key={voucher.id}>
                <CouponContainer {...voucher} />
              </S.ListItem>
            ))}
          {filterTypes.activated &&
            allActivated.map(voucher => (
              <S.ListItem key={voucher.id}>
                <CouponContainer {...voucher} />
              </S.ListItem>
            ))}
        </S.ListContainer>
      </Transition.Fade>

      <Transition.Fade>
        <LoadMore
          onClick={getMoreVouchers}
          hasMoreContent={hasMoreContent}
          isLoading={percentagePaginated.isRequest}
        />
      </Transition.Fade>
    </Stack>
  )
}

export default ListVouchers
